import { createElement, useCallback, useState } from "react";
import { createPortal } from "react-dom";

/**
 * `<sc-toaster>` — die kurzen Meldungen unten in der Ecke.
 *
 * Kein Custom Element mehr, aber derselbe Tag am selben Ort (`<body>`, per
 * Portal) und **dieselbe Schnittstelle**: `modules/toast.ts` ruft
 * `document.querySelector("sc-toaster").show(message, type, duration)`, und
 * diese Methode hängt am Element, solange die Komponente steht. Die
 * Hauptwurzel (main.tsx) zeichnet sie einmal — für Anmeldeseite und
 * Oberfläche gleichermaßen.
 */
type Toast = { id: number; message: string; type: string };
type Show = (message: string, type?: string, duration?: number) => void;

/** Laufende Nummer der Meldungen — es gibt nur einen Toaster auf der Seite. */
let toastSeq = 0;

const ICONS: Record<string, string> = { success: "✓", error: "✕", warning: "⚠", info: "ℹ" };

export function Toaster() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // `show` hängt am Element, sobald es im Dokument steht — im Ref-Rückruf,
  // also noch im selben Durchlauf: boot.tsx zeichnet synchron und meldet gleich
  // danach die Rückkehr von einer Anmeldung (connect-return).
  const attach = useCallback((host: HTMLElement | null) => {
    if (!host) return;
    const show: Show = (message, type = "info", duration = 4000) => {
      toastSeq += 1;
      const id = toastSeq;
      setToasts((list) => [...list, { id, message: String(message), type }]);
      setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), duration);
    };
    Reflect.set(host, "show", show);
    return () => { Reflect.deleteProperty(host, "show"); };
  }, []);

  return createPortal(
    createElement("sc-toaster", { ref: attach, className: "sc-toaster" }, toasts.map((toast) => (
      <div key={toast.id} className={`toast toast--${toast.type}`}>
        <span className="toast__icon">{ICONS[toast.type] ?? ""}</span>
        <span>{toast.message}</span>
      </div>
    ))),
    document.body,
  );
}
